import { useEffect, useLayoutEffect, useState } from 'react';
import type { BaseRange } from 'slate';
import { ReactEditor } from 'slate-react';

import { getOffsetWithin } from '../lib/utils';

export interface HighlightRange {
  target: string;
  range: BaseRange;
}

interface Params {
  editor: ReactEditor;
  rootElement: HTMLElement | null;
  highlights: HighlightRange[];
  isVisible: boolean;
}

// The vertical centre of the line each highlight ends on, by target.
export type HighlightEnds = Record<string, number>;

const measureLastLine = (editor: ReactEditor, rootElement: HTMLElement, range: BaseRange) => {
  try {
    const lines = [...ReactEditor.toDOMRange(editor, range).getClientRects()].filter(
      (rect) => rect.width > 0
    );
    const last = lines.at(-1);
    if (!last) {
      return null;
    }
    const { top, bottom } = getOffsetWithin(rootElement, last);
    return (top + bottom) / 2;
  } catch {
    return null;
  }
};

/**
 * Where the comment bubbles go in the right margin. The text wraps differently on every width, so
 * it is measured again when the text, the highlights or the width change. A hidden view measures
 * zeros, so it is measured again when it becomes visible.
 */
export const useDocumentLayout = ({ editor, rootElement, highlights, isVisible }: Params) => {
  const [highlightEnds, setHighlightEnds] = useState<HighlightEnds>({});
  const [layoutRevision, setLayoutRevision] = useState(0);

  useEffect(() => {
    if (!rootElement) {
      return undefined;
    }
    const observer = new ResizeObserver(() => setLayoutRevision((value) => value + 1));
    observer.observe(rootElement);
    return () => observer.disconnect();
  }, [rootElement]);

  useLayoutEffect(() => {
    if (!rootElement || !isVisible) {
      return;
    }

    const ends: HighlightEnds = {};
    highlights.forEach(({ target, range }) => {
      const centre = measureLastLine(editor, rootElement, range);
      if (centre !== null) {
        ends[target] = centre;
      }
    });
    setHighlightEnds(ends);
  }, [editor, rootElement, highlights, isVisible, layoutRevision]);

  return highlightEnds;
};
